import {ShopContainer} from "../ShopContainer";
import {ProjectActions} from "../ProjectActions";
import React, {useEffect, useRef} from "react";
import {CheckboxProvider} from "../Checkbox";
import {grey} from "@mui/material/colors";
import {Grid, Tab, Tabs} from "@mui/material";
import {tabGroup} from "../../../logistics/purchasing/PartsToSource";
import {Openings} from "./Openings";
import { Damaged } from "./Damaged";
import {EventEmitter, useAsync2} from "nate-react-api-helpers";
import {api} from "../../../../api/API";
import {useProjectId} from "../ProjectName";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {defaultTabHostClass, useDefaultTab} from "../../../../misc/userpref/useDefaultTab";
import {Div10} from "./Div10";
import {projectTablePrefName} from "../TablePrefName";
import {Waste} from "./Waste";
import {ProjectPageTitle} from "../ProjectPageTitle";
import {isPriceRequestTab, PriceRequestSubmittal} from "./PriceRequestSubmittal";
import {isLogisticsRecordsTab, LogisticsRecordsSubmittal} from "./LogisticsRecordsSubmittal";
import {LogisticsActions} from "./LogisticsActions";
import {useLogisticsView, ViewButton} from "./ViewButton";
import {WarnUnresolvedPropsedChanges} from "./WarnUnresolvedPropsedChanges";
import {WarnMissingScreenElevation} from "./WarnMissingScreenElevation";

export type LogisticsTab = "frame-hm" | "frame-anchor" | "door-hm" | "door-wd" | "hardware" | "glazing" | "div10" | "component" | "damaged" | "waste";

export function logisticsTableName(project: number, type: string) {
    return projectTablePrefName("project.logistics." + type, project)
}

export const damagedEmitter = new EventEmitter<{count: number}>();
export const wasteEmitter = new EventEmitter<{count: number}>();

export function Logistics() {
    const params = useParams<{subspace: string}>();
    const navigate = useNavigate();
    const loc = useLocation();
    const project = useProjectId();

    const dt = useDefaultTab({
        page: "shopdrawing.logistics", fallback: "frame-hm",
        allowed: ["frame-hm", "frame-anchor", "door-hm", "door-wd", "hardware", "glazing", "div10", "component", "damaged", "waste"],
    });

    // a path without a subspace means frame-hm for everyone. A stored default is
    // written into the path once per page visit instead.
    const appliedFor = useRef<string | null>(null);
    const pendingDefault = !params.subspace && dt.defaultValue !== "frame-hm" && appliedFor.current !== loc.pathname;
    const tab = (params.subspace || (pendingDefault ? dt.defaultValue : "frame-hm")) as LogisticsTab;

    useEffect(() => {
        if (!pendingDefault) return;
        appliedFor.current = loc.pathname;
        navigate({
            pathname: `/project/${project}/shop-drawing/logistics/` + dt.defaultValue,
            search: loc.search,
        }, {replace: true});
    }, [pendingDefault, loc.pathname, loc.search, navigate, project, dt.defaultValue]);

    const [view, setView] = useLogisticsView();
    const damageList = useAsync2(input => api.logistics.listDamagedProducts(input), {includeReviewed: false, project}, [project]);
    const wasteList = useAsync2(input => api.logistics.listWasteProducts(input), {includeReviewed: false, project}, [project]);

    useEffect(() => {
        damagedEmitter.emit({count: damageList.result?.count || 0});
    }, [damageList.result?.count])

    useEffect(() => {
        wasteEmitter.emit({count: wasteList.result?.count || 0});
    }, [wasteList.result?.count])

    const damagedCt = EventEmitter.reactValue(damagedEmitter) || {count: 0}
    const wasteCt = EventEmitter.reactValue(wasteEmitter) || {count: 0}

    const isRelease = view === "release";

    return (
        <CheckboxProvider>
        <ShopContainer name={
            // match the padding of the Button title on other shop pages (see QuoteSwitcher)
            <div style={{paddingLeft: 8}}>{isRelease ? "Logistics (Release)" : "Logistics"}</div>
        } actions={<>
            {isLogisticsRecordsTab(tab) && <LogisticsRecordsSubmittal tab={tab} />}
            {isPriceRequestTab(tab) && <PriceRequestSubmittal tab={tab} />}
            {(tab !== "damaged" && tab !== "waste") && <Grid item>
                <LogisticsActions type={tab} view={view} />
            </Grid>}
        </>} overrideRightActions={<div style={{display: "flex", alignItems: "flex-end"}}>
            <div style={{marginTop: 4}}>
                <ViewButton value={view} onChange={setView} />
            </div>
            <ProjectActions />
        </div>}>
            <ProjectPageTitle
                title={isRelease ? "Release" : "Logistics"}
            />
                <div style={{backgroundColor: grey["200"]}} className={defaultTabHostClass}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <Tabs className={tabGroup} value={tab} onChange={(e, value) => {
                                navigate({
                                    pathname: `/project/${project}/shop-drawing/logistics/` + value,
                                    search: loc.search,
                                });
                            }}>
                                <Tab label="HM Frame" value="frame-hm" {...dt.tabProps("frame-hm")} />
                                <Tab label="Anchors" value="frame-anchor" {...dt.tabProps("frame-anchor")} />
                                <Tab label="HM Door" value="door-hm" {...dt.tabProps("door-hm")} />
                                <Tab label="WD Door" value="door-wd" {...dt.tabProps("door-wd")} />
                                <Tab label="Hardware" value="hardware" {...dt.tabProps("hardware")} />
                                <Tab label="Glazing" value="glazing" {...dt.tabProps("glazing")} />
                                <Tab label="Div 10" value="div10" {...dt.tabProps("div10")} />
                                <Tab label="Component" value="component" {...dt.tabProps("component")} />
                                <Tab label={damagedCt.count > 0 ? `Damaged (${damagedCt.count})` : "Damaged"} value="damaged" {...dt.tabProps("damaged")} />
                                <Tab label={wasteCt.count > 0 ? `Waste (${wasteCt.count})` : "Waste"} value="waste" {...dt.tabProps("waste")} />
                                <div style={{height: "2rem"}} />
                            </Tabs>
                        </Grid>
                        <Grid item style={{marginLeft: "auto", display: "flex"}}>
                            {dt.controls}
                        </Grid>
                    </Grid>
                </div>
                {isRelease && <WarnUnresolvedPropsedChanges />}
                {isRelease && (tab === "frame-hm" || tab === "door-hm" || tab === "door-wd") && <WarnMissingScreenElevation />}
                {tab === "damaged" ? <Damaged project={project} /> :
                    tab === "waste" ? <Waste project={project} /> :
                        tab === "div10" ? <Div10 view={view} /> :
                            <Openings view={view} type={tab} />}
        </ShopContainer>
        </CheckboxProvider>
    )
}
